import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';

import {
  useDataRetentionDurationQuery,
  useRunArtifactVersionsQuery,
} from '../generated/graphql';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from './Banner';
import * as S from './DeleteRunModal.styles';
import RadioForm from './RadioForm';

const DAY = 24 * 3600 * 1000;

interface DeleteRunModalProps {
  entityName: string;
  projectName: string;
  runName: string;
  runDisplayName: string;
  isRunActive: boolean;
  trigger?: React.ReactNode;
  onModalCanceled?(): void;
  onDeleteRun(deleteArtifacts?: boolean): void;
}

const DeleteRunModal: React.FC<DeleteRunModalProps> = React.memo(
  ({
    entityName,
    projectName,
    runName,
    runDisplayName,
    isRunActive,
    trigger,
    onModalCanceled,
    onDeleteRun,
  }) => {
    const runArtifacts = useRunArtifactVersionsQuery({
      variables: {
        entityName,
        projectName,
        runName,
        includeOutput: true,
        includeInput: false,
        pageSize: 1, // we only care about the total count, so keep the size low
      },
      fetchPolicy: 'no-cache',
    });

    const outputArtifactsCount =
      runArtifacts.data?.project?.run?.outputArtifacts?.totalCount ?? 0;
    const outputArtifacts =
      runArtifacts.data?.project?.run?.outputArtifacts?.edges;
    let countArtifactsUsedBy = 0;
    outputArtifacts?.forEach(artifact => {
      if (artifact.node?.usedBy.totalCount) {
        countArtifactsUsedBy += artifact.node?.usedBy.totalCount;
      }
    });

    const showCheckbox = (outputArtifactsCount ?? 0) > 0;
    const [deleteArtifacts, setDeleteArtifacts] = useState('');

    const dataRetentionDurationQuery = useDataRetentionDurationQuery({});
    const dataRetentionDurationMs =
      dataRetentionDurationQuery.data?.serverInfo?.dataRetentionDuration ??
      7 * DAY;
    const dataRetentionDays = Math.ceil(dataRetentionDurationMs / DAY);

    return (
      <S.StyledWBModal
        open
        theme="danger"
        header={`Delete run`}
        onClose={onModalCanceled}
        includeCancel
        cancelButtonText="Nevermind"
        primaryAction={{
          content: isRunActive ? `Yes, delete run` : `Delete run`,
          disabled: !deleteArtifacts && showCheckbox,
          onClick: () => onDeleteRun(deleteArtifacts === 'true'),
        }}>
        {isRunActive ? (
          <p>
            You’re deleting an active run, which might crash live processes if
            your run is actually still running. We suggest that you stop the run
            before deleting it.
          </p>
        ) : (
          <p>
            Delete run "{runDisplayName}"? This affects all pages, including
            reports.{' '}
            {dataRetentionDays > 0
              ? `This run will be permanently deleted and unrecoverable in 
              ${dataRetentionDays} day${dataRetentionDays > 1 ? 's' : ''}.`
              : `This run will be unrecoverable after 7 days.`}
          </p>
        )}
        {showCheckbox && (
          <RadioForm
            name="deleteArtifactsGroup"
            options={[
              {
                label: `Also delete the ${outputArtifactsCount} artifact${
                  outputArtifactsCount > 1 ? 's' : ''
                } this run created`,
                key: 'true',
              },
              {label: 'Keep the artifacts', key: 'false'},
            ]}
            value={deleteArtifacts}
            onChange={newValue => setDeleteArtifacts(newValue)}
          />
        )}
        {deleteArtifacts && countArtifactsUsedBy > 0 && (
          <Banner variant="error">
            <BannerFlexWrapper>
              <Icon name="warning" style={{flex: 'none'}} />
              <BannerTextWrapper>{`Are you sure you want to delete all artifacts this run produces? ${countArtifactsUsedBy} other runs use these artifacts as input (or reference these artifacts) and will no longer be reproducible.`}</BannerTextWrapper>
            </BannerFlexWrapper>
          </Banner>
        )}
      </S.StyledWBModal>
    );
  }
);

export default DeleteRunModal;
